/******************************************
*  Author : Riccardo Di Dato
*  Created On : Thu Dec 30 2021
*  File : Observable.ts
*******************************************/
import { Observer } from "./Observer.js";
import { Subscription } from "./Subscription.js";
/**
 * Classe che implementa il pattern publisher and subscriber, ispirata agli observable di RxJS.
 *
 * @example
 * ```ts
 * const x = new Observable<string>( (obs) => {
 *      obs.next("qualcosa")
 * });
 *
 * x.subscribe(v => {
 *  console.log(v);
 * })
 * ```
 * In console avremo
 * > "qualcosa"
 */
export class Observable {
    _fn;
    /**
     * Definisce la meccanica del publish
     *
     * @param fn Funzione per effettuare il publish (vedi {@link Observer})
     */
    constructor(fn) {
        this._fn = fn;
    }
    /**
     * Permette di iscrivere dei callback alla classe
     *
     * @note In caso di observable è più o meno impossibile disiscriversi in tempo dato che l'esecuzione avviene contestualmente all'iscrizione
     * @param onNext callback per aggiornamenti del valore
     * @param onErr callback per errori
     * @param onComplete callback per completamento
     */
    subscribe(onNext, onErr = () => { }, onComplete = () => { }) {
        const obs = new Observer(onNext, onErr, onComplete);
        this._fn(obs);
        return new Subscription(() => {
            obs.unsubscribe();
        });
    }
    /**
     * Permette di applicare una funzione di trasformazione all'Observable.
     *
     * @note A differenza del `pipe` di RxJS questo applica sempre l'equivalente di un map
     *
     * @example
     * ```ts
     * const x = new Observable<string>( (obs) => {
     *      obs.next("qualcosa")
     * });
     *
     * x.pipe(
     *      v => v + " in più"
     * ).subscribe(v => {
     *      console.log(v);
     * })
     * ```
     * In console avremo
     * > "qualcosa in più"
     */
    pipe(onNext) {
        return new Observable((observer) => {
            this.subscribe((ret) => {
                observer.next(onNext(ret));
            }, (err) => {
                observer.error(err);
            }, () => {
                observer.complete();
            });
        });
    }
    /**
     * Restituisce una promise che si risolve con l'ultimo valore emesso dall'observable prima del complete
     * Funziona come `lastValueFrom` di [RxJS](https://rxjs.dev/api/index/function/lastValueFrom)
     * @returns
     */
    lastValueFrom() {
        let resolve, reject, retval;
        const rval = new Promise((innerResolve, innerReject) => {
            resolve = innerResolve;
            reject = innerReject;
        });
        this.subscribe(n => {
            retval = n;
        }, e => {
            reject(e);
        }, () => {
            resolve(retval);
        });
        return rval;
    }
    /**
     * Restituisce una promise che si risolve con il prossimo valore emesso dall'observable
     * Funziona come `firstValueFrom` di [RxJS](https://rxjs.dev/api/index/function/firstValueFrom)
     * @returns
     */
    firstValueFrom() {
        let resolve, reject;
        const rval = new Promise((innerResolve, innerReject) => {
            resolve = innerResolve;
            reject = innerReject;
        });
        this.subscribe(n => {
            resolve(n);
        }, e => {
            reject(e);
        });
        return rval;
    }
}
